import { styled } from '@mui/material/styles';
import { Box, Button, Table, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

// ============================================
// Layout
// ============================================
export const PageWrapper = styled(Box)({
  boxShadow: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
  '& > *:last-child': {
    marginTop: '16px',
  },
});

// ============================================
// Summary Cards (Figma node 4912:7140)
// 4개의 요약 카드 — 결제/할인/환불/미수.
// 카드별로 강조 색상(value 텍스트)이 다르고, 환불/미수는 회색 배경.
// flex: 1 로 컨테이너 가로폭을 균등 분할.
// ============================================
export const SummaryCardsRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '16px',
});

// variant: 'teal' (결제/할인) | 'gray' (환불/미수)
export const SummaryCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})(({ variant }) => ({
  flex: 1,
  minWidth: 0,
  height: '120px',
  padding: '16px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  backgroundColor: variant === 'teal' ? '#F3F9FA' : '#F6F7F9',
}));

export const SummaryCardTitle = styled(Typography)({
  width: '100%',
  textAlign: 'center',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  color: '#595959',
  lineHeight: 'normal',
});

// valueColor: 'teal' (결제금액) | 'orange' (환불/미수) | 'default' (할인/양도)
export const SummaryCardValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'valueColor',
})(({ valueColor }) => ({
  width: '100%',
  textAlign: 'center',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: 'normal',
  color: valueColor === 'teal' ? '#4AB3BC' : valueColor === 'orange' ? '#EE735A' : '#595959',
}));

export const SummaryCardCount = styled(Typography)({
  width: '100%',
  textAlign: 'center',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  color: '#595959',
  lineHeight: 'normal',
});

// ============================================
// Total Count
// ============================================
export const TotalCount = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#555',
  marginTop: '20px',
  marginBottom: '-12px',
});

// ============================================
// Payment Table (하단 결제 내역 테이블)
// ============================================
export const PaymentTableContainer = styled(TableContainer)({
  marginTop: '20px',
  borderRadius: '8px',
  overflowX: 'auto',
  overflowY: 'auto',
  width: '100%',
  height: 'auto',
  maxHeight: '540px',
  border: '1px solid #e8e8e8',
});

export const PaymentTable = styled(Table)({
  minWidth: 1600,
  tableLayout: 'fixed',
  width: '2000px',
});

export const PaymentHead = styled(TableHead)({
  background: '#F0F2F5',
  position: 'sticky',
  top: 0,
  zIndex: 100,
});

export const PaymentHeadRow = styled(TableRow)({
  height: '44px',
});

export const PaymentHeadCell = styled(TableCell, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }) => ({
  fontWeight: 600,
  fontSize: '13px',
  color: '#333',
  padding: '8px',
  borderBottom: 'none',
  ...(width && { width }),
}));

// 결제 상태별 행 배경 톤 — 매출관리(SalesManagTab1Styled)와 같은 값을 쓴다.
// 취소 행은 기존 회색(비활성 표현)이 우선이므로 톤을 덮지 않는다.
const ROW_TONE_BG = {
  refund: { base: '#fdf3f1', hover: '#fbe9e6' }, // 환불 행 — 붉은 기
  outstanding: { base: '#fff8ef', hover: '#fdf0df' }, // 미수 잔액이 남은 행 — 주황 기
};

export const PaymentBodyRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'canceled' && prop !== 'rowTone',
})(({ canceled, rowTone }) => {
  const tone = !canceled && ROW_TONE_BG[rowTone];
  return {
    height: '44px',
    textDecoration: 'none',
    ...(tone && { backgroundColor: tone.base }),
    ...(canceled && {
      backgroundColor: '#f9f9f9',
      '& td': {
        color: '#bbb',
      },
    }),
    '&:hover': {
      backgroundColor: canceled ? '#f3f3f3' : tone ? tone.hover : '#f9fafb',
      '& .row-actions': {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
  };
});

// 모든 셀 — 한 줄 고정 + 넘치면 ellipsis.
export const PaymentBodyCell = styled(TableCell)({
  fontSize: '13px',
  padding: '8px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 0,
});

// 회원권명 셀 — 호버 시 액션 버튼이 위에 겹쳐 표시됨.
export const TicketNameCell = styled(TableCell)({
  fontSize: '13px',
  padding: '8px',
  position: 'relative',
  overflow: 'visible',
  maxWidth: 0,
  '& > span': {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

// 결제상태 칩 — 매출관리(SalesManagTab1Styled STATUS_CHIP_COLORS)와 같은 팔레트를 쓴다.
// 두 화면에서 같은 색이 같은 의미가 되도록 통일한 값이다.
const STATUS_STYLES = {
  PAID: { background: '#e6f2f3', color: '#2f8f97' }, // 결제
  PAID_OUTSTANDING: { background: '#fde8cc', color: '#b26a00' }, // 미수금결제
  PAID_CHANGE: { background: '#e6f2f3', color: '#2f8f97' }, // 변경결제도 일반 결제와 같은 표시(사용자 결정)
  REFUNDED: { background: '#fbe0da', color: '#c0392b' }, // 환불
  CANCELED: { background: '#e8e8e8', color: '#6b6b6b' }, // 취소
  OUTSTANDING: { background: '#fde8cc', color: '#b26a00' }, // 미수 잔액 남음
};

export const StatusChip = styled(Typography, {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.PAID;
  return {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    backgroundColor: style.background,
    color: style.color,
  };
});

// 데이터 없을 때 빈 상태 셀.
export const EmptyCell = styled(TableCell)({
  padding: '60px 0',
  textAlign: 'center',
  color: '#9e9e9e',
  fontSize: '14px',
});

// ============================================
// Row Action Buttons (행 호버 시 수강권 셀 위에 표시)
// ============================================
export const RowActions = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  backgroundColor: 'inherit',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 0.12s ease',
});

// "상세/환불내역" 버튼 — teal outline.
export const ViewButton = styled(Button)({
  background: '#fff',
  color: '#4AB3BC',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  minWidth: 'auto',
  padding: '3px 10px',
  fontSize: '12px',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    background: '#F0F9FA',
  },
});

// "수정" 버튼 — teal 채움.
export const EditButton = styled(Button)({
  background: '#4AB3BC',
  color: '#fff',
  border: '1px solid #4AB3BC',
  borderRadius: '6px',
  minWidth: 'auto',
  padding: '3px 10px',
  fontSize: '12px',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    background: '#3EA0A8',
  },
});
